import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquareReply, ScanLine, Users, MessageCircle, BarChart3, Smartphone, Repeat2, Receipt, Infinity as InfinityIcon,
  Check, FileText, Mail, Landmark,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { usePricing, dkk } from "../lib/pricing";
import { SiteShell, FaqItem, faqsFor } from "./Landing";
import Testimonials from "../components/Testimonials";

const GROUPS = [
  ["robot", MessageSquareReply, "Facebook-robot", ["Svarer automatisk på \"ja tak\"", "Synes godt om kundens kommentar", "Bekræftelse og afhentningskode i Messenger",
    "Varianter, fx farver og størrelser", "Automatisk venteliste", "Håndterer rettede og slettede kommentarer", "Opslag på jeres side og automatisk \"UDSOLGT\""]],
  ["pickup", ScanLine, "Udlevering", ["Personlige afhentningskoder", "QR-scanning med telefonen", "Fortryd en udlevering", "Markér \"ikke hentet\"",
    "Markering af overskredne afhentninger", "Plukliste til pakning"]],
  ["customers", Users, "Kunder", ["Kundeoversigt med køb og afhentningsrate", "Noter på kunder", "Udeblivelser og automatisk blokering",
    "Bekræftelsesregel for kunder, der ikke hentede (kan slås til)", "Kundens egen ordreside med kalender og afbud"]],
  ["sms", MessageCircle, "SMS-påmindelser", ["150 SMS pr. butik pr. måned er inkluderet", "Ekstra SMS 0,50 kr. ekskl. moms – kun hvis I selv slår det til", "Ingen SMS i prøveperioden", "Til kunder, der selv har tilmeldt deres mobilnummer", "Slå hver type SMS til eller fra"]],
  ["stats", BarChart3, "Statistik", ["Omsætning pr. dag, uge og måned", "Udvikling i forhold til forrige periode", "Bedst sælgende varianter", "Hvornår kunderne siger ja tak"]],
  ["app", Smartphone, "App på telefonen", ["Installeres direkte fra browseren", "Virker på iPhone og Android"]],
  ["reuse", Repeat2, "Genbrug tilbud", ["Brug et afsluttet tilbud igen som ny kladde med ny afhentningstid"]],
  ["billing", Receipt, "Fakturering og dansk support", ["Månedlig faktura på mail – hent den som PDF", "Dansk support på mail"]],
  ["unlimited", InfinityIcon, "Ubegrænset", ["Ubegrænset antal tilbud og ordrer"]],
];

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Priser – JaTak Pro";
    const set = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const old = el?.getAttribute("content");
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
      return () => (old == null ? el.remove() : el.setAttribute("content", old));
    };
    const undo = [
      set("name", "description", "Én fast pris for JaTak Pro – ja tak-robot til Facebook. Alt inkluderet, ingen binding, 0 kr. i gebyr pr. salg og gratis prøveperiode."),
      set("property", "og:title", "Priser – JaTak Pro"),
    ];
    return () => { document.title = prev; undo.forEach((u) => u()); };
  }, []);
}

function Cta({ store, trial, testid }) {
  if (store) {
    return (
      <div className="pr-cta">
        <Link className="btn jatak big" to={store.is_owner ? "/owner" : "/overblik"} data-testid={`${testid}-overview`}>Gå til Overblik</Link>
        <Link className="btn big" to="/billing" data-testid={`${testid}-billing`}>Se dit abonnement</Link>
      </div>
    );
  }
  return (
    <div className="pr-cta">
      <Link className="btn jatak big" to="/signup" data-testid={`${testid}-signup`}>Prøv gratis i {trial} dage</Link>
    </div>
  );
}

export default function PricingPage() {
  useSeo();
  const p = usePricing();
  const { store } = useAuth();
  const faqs = faqsFor(p, "pricing");
  return (
    <SiteShell>
      <section className="pr-hero" data-testid="pricing-hero">
        <span className="eyebrow">Priser</span>
        <h1>Én pris. Alt inkluderet.</h1>
        <div className="pr-price" data-testid="pricing-price">
          <span className="pr-num" data-testid="pricing-price-value">{dkk(p.price_monthly_dkk)}</span>
          <span className="pr-unit">kr./md.</span>
          <span className="pr-ex">ekskl. moms</span>
        </div>
        <p className="pr-incl" data-testid="pricing-price-incl">{dkk(p.price_monthly_incl_vat)} kr. inkl. {p.vat_pct} % moms</p>
        <ul className="pr-perks" data-testid="pricing-perks">
          <li><Check size={16} /> {p.trial_days} dages gratis prøve</li>
          <li><Check size={16} /> Ingen binding</li>
          <li><Check size={16} /> Ingen gebyr pr. salg</li>
          <li><Check size={16} /> Månedlig faktura</li>
        </ul>
        <Cta store={store} trial={p.trial_days} testid="pricing-hero-cta" />
      </section>

      <section className="pr-sec" data-testid="pricing-included">
        <div className="sec-head">
          <span className="eyebrow">Alt-i-én</span>
          <h2>Det er med i prisen</h2>
          <p>Ingen pakker – alle butikker får det hele. Det eneste tilkøb er ekstra SMS, og kun hvis I selv slår det til.</p>
          <p><Link to="/funktioner" className="pr-more" data-testid="pricing-features-link">Se alle funktioner →</Link></p>
        </div>
        <div className="pr-groups">
          {GROUPS.map(([k, Icon, title, items]) => (
            <div className="pr-group" key={k} data-testid={`pricing-group-${k}`}>
              <div className="feature-ico"><Icon size={20} /></div>
              <h3>{title}</h3>
              <ul>{items.map((t) => <li key={t}><Check size={15} /> {t}</li>)}</ul>
            </div>
          ))}
        </div>
      </section>

      <section className="pr-sec pr-value" data-testid="pricing-value">
        <div className="pr-value-num"><span className="pr-num">0</span><span className="pr-unit">kr.</span></div>
        <div>
          <h2>i gebyr pr. salg</h2>
          <p>Uanset om I sælger 10 eller 10.000 varer, betaler I kun de faste {dkk(p.price_monthly_dkk)} kr. om måneden. Vi tager ingen procent af jeres omsætning.</p>
        </div>
      </section>

      <Testimonials testid="pricing-testimonials" />

      <section className="pr-sec" data-testid="pricing-payment">
        <div className="sec-head">
          <span className="eyebrow">Betaling</span>
          <h2>Sådan fungerer betaling</h2>
        </div>
        <ol className="pr-steps">
          <li><span className="pr-step-ico"><FileText size={20} /></span><b>Start abonnement</b><span>Udfyld firmaoplysninger under Abonnement – når som helst i prøveperioden.</span></li>
          <li><span className="pr-step-ico"><Mail size={20} /></span><b>Faktura på mail</b><span>I får en faktura på mail hver måned – hent den som PDF – med {p.due_days} dages betalingsfrist.</span></li>
          <li><span className="pr-step-ico"><Landmark size={20} /></span><b>Betal via bankoverførsel</b><span>Overfør beløbet til kontoen på fakturaen. Intet kort, ingen automatisk træk.</span></li>
        </ol>
      </section>

      <section className="faq pr-sec" data-testid="pricing-faq">
        <div className="sec-head">
          <span className="eyebrow">FAQ</span>
          <h2>Spørgsmål om pris og betaling</h2>
        </div>
        <div className="faq-grid">
          {faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      <section className="cta-band" data-testid="pricing-final-cta">
        <span className="eyebrow">Klar til at prøve?</span>
        <h2>Prøv hele JaTak Pro gratis i {p.trial_days} dage</h2>
        <p>Ingen binding og intet kort. Test det hele i testtilstand – helt uden at forbinde Facebook.</p>
        <Cta store={store} trial={p.trial_days} testid="pricing-final-cta" />
      </section>
    </SiteShell>
  );
}
